<template>
    <div>
        <b-row>
            <b-col>
                <b-row>
                    <b-col cols="3">
                        <strong>Subject</strong>
                    </b-col>
                    <b-col cols="9">
                        <template v-if="editMode">
                            <b-form-group :state="errorState('subject')"
                                          invalid-feedback="Required">
                                <b-input id="notification-subject"
                                         v-model="notification.workspace.subject"
                                         placeholder="Subject"
                                         :state="errorState('subject')"
                                         size="sm" />
                            </b-form-group>
                        </template>
                        <template v-else>{{notification.subject}}</template>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col cols="3">
                        <strong>Notice</strong>
                    </b-col>
                    <b-col cols="9">
                        <template v-if="editMode">
                            <b-form-group :state="errorState('note')"
                                          invalid-feedback="Required">
                                <RichTextEditor id="notification-note"
                                                v-model="notification.workspace.note"
                                                :state="errorState('note')"
                                                size="sm" />
                            </b-form-group>
                        </template>
                        <template v-else><span v-html="notification.note" /></template>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col cols="3">
                        <strong>Publish Date</strong>
                    </b-col>
                    <b-col cols="9">
                        <template v-if="editMode">
                            <b-form-group :state="errorState('publishDate')"
                                          invalid-feedback="Required">
                                <PickerOfDates type="datetime"
                                               format="M/d/yyyy h:mm a"
                                               id="notification-published"
                                               v-model="notification.workspace.publishDate"
                                               :state="errorState('publishDate')"
                                               :time-picker-options="timePickerOptions"
                                               size="sm" />
                            </b-form-group>
                        </template>
                        <template v-else>{{notification.publishDate | formatDate}}</template>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col cols="3">
                        <strong>Expiration Date</strong>
                    </b-col>
                    <b-col cols="9">
                        <template v-if="editMode">
                            <b-form-group :state="errorState('expirationDate')"
                                          invalid-feedback="Required (expiration date must be after publish date)">
                                <PickerOfDates type="datetime"
                                               format="M/d/yyyy h:mm a"
                                               id="notification-expiration"
                                               v-model="notification.workspace.expirationDate"
                                               :state="errorState('expirationDate')"
                                               :time-picker-options="timePickerOptions"
                                               size="sm" />
                            </b-form-group>
                        </template>
                        <template v-else>{{notification.expirationDate | formatDate}}</template>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <div v-if="!notification.isNew"
                             style="text-align: right">
                            Created by {{notification.createdByUsername}} {{notification.createdDate | formatDate}}
                        </div>
                        <div v-if="notification.hasUpdate"
                             style="text-align: right">
                            Updated by  {{notification.updatedByUsername}} {{notification.updatedDate | formatDate}}
                        </div>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <br />
                        <b-button-group class="float-right">
                            <template v-if="!editMode && canEdit">
                                <b-button variant="dark"
                                          @click="edit">Edit</b-button>
                            </template>
                            <template v-else-if="editMode">
                                <b-button variant="warning"
                                          @click="cancel">Cancel</b-button>
                                <b-button v-if="canDelete"
                                          variant="danger"
                                          @click="deleteSave">Delete</b-button>
                                <b-button variant="success"
                                          @click="save">Save</b-button>
                            </template>
                        </b-button-group>
                    </b-col>
                </b-row>
                
            </b-col>
        </b-row>
    </div>
</template>


<script>
    import {Component, Prop, Vue} from 'vue-property-decorator';
    import RichTextEditor from '@/components/shared/RichTextEditor.vue';
    import PickerOfDates from '@/components/shared/PickerOfDates';
    import {NULL_USER_NOTIFICATION, UserNotification} from "@/model/notification";
    import {date, errorToastOptions} from '@/util/formatters';

    @Component({
        filters: {
            formatDate: (d) => date(d, 'M/d/yyyy h:mm a'),
        },
        components: {
            RichTextEditor,
            PickerOfDates
        }
    })

    export default class UserNotificationDetail extends Vue {
        @Prop({type: UserNotification, default: function() { return NULL_USER_NOTIFICATION; }}) notification;

        editMode = false;

        get timePickerOptions() {
            return {
                start: '00:00',
                step: '00:15',
                end: '23:45',
                format: 'h:mm a'
            };
        }

        errorState(field) {
            return !this.notification.workspace.errors[field];
        }

        edit() {
            // console.log(this.notification);
            this.editMode = true;
        }

        done() {
            this.$emit('done');
            this.editMode = false;
        }

        cancel() {
            this.notification.rollback();
            this.done();
        }

        async deleteSave() {
            const check = await this.$bvModal.msgBoxConfirm(
                'Are you sure you wish to delete this user notification?', {
                    title: 'Confirm Delete',
                    noCloseOnBackdrop: true,
                    noCloseOnEsc: true,
                    headerBgVariant: 'dark',
                    headerTextVariant: 'white'
                });
            if (!check) {
                return;
            }
            this.notification.workspace.deleted = true;
            await this.save();
        }

        async save() {
            try {
                await this.$store.dispatch('notification/processUserNotification', this.notification.workspace);
                if (this.notification.isNew) {
                    this.notification.rollback();
                    this.updateEditMode();
                }
                else {
                    this.notification.commit();
                    this.editMode = false;
                }
                this.$emit('save');
            }
            catch (error) {
                this.$bvToast.toast(error.message, errorToastOptions);
            }
        }

        get canEdit() {
            //Notifications are editable by admin users, regardless of original author
            const loggedInUser = this.$store.getters['userSession/getUser'];
            return loggedInUser.isAnAdministrator();
        }

        get canDelete() {
            //Can only delete a non-new notification
            return !this.notification.isNew;
        }

        updateEditMode() {
            //Only default to edit mode for new notes
            this.editMode = this.notification.isNew && this.canEdit;
        }

        mounted() {
            // console.log(this.notification);
            this.updateEditMode();
        }
    }
</script>


<style scoped>
</style>
<!--<style>-->
<!--    /*Known bug between CKEditor5 and Bootstrap modals - modal blocks RTE functions*/-->
<!--    /*Below solution displays link dialogue but selection cannot be made*/-->
<!--    /*Note that listening for editor events to toggle no-enforce-focus on modal doesn't work either*/-->
<!--    body {-->
<!--        &#45;&#45;ck-z-default: 100;-->
<!--        &#45;&#45;ck-z-modal: calc( var(&#45;&#45;ck-z-default) + 999 );-->
<!--    }-->
<!--</style>-->